<template>
  <transition name="fade">
    <base-modal v-if="visible" width="920px" height="85vh" @close="handleClose">
      <template #header>
        <div class="mcl-modal-header">
          <div class="mcl-modal-header-inner">
            <div class="mcl-modal-header-icon-wrap" :style="{ backgroundColor: headerAccentBg }">
              <font-awesome-icon :icon="['fas', 'users']" class="mcl-modal-header-icon" />
            </div>
            <div class="text-left">
              <h2 class="mcl-modal-header-title">{{ modalTitle }}</h2>
              <p class="mcl-modal-header-sub">
                {{ activeTabLabel }} · {{ paginatorInfo && paginatorInfo.total ? paginatorInfo.total : 0 }} {{ tasksCountLabel }}
                <span v-if="paginatorInfo && paginatorInfo.total"> · page {{ paginatorInfo.currentPage }} of {{ paginatorInfo.lastPage }}</span>
              </p>
            </div>
          </div>
        </div>
      </template>

      <div class="mcl-modal-body mbst-modal-body-flex">
        <div class="mbpt-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.key"
            type="button"
            class="mbpt-tab-btn"
            :class="{ 'mbpt-tab-btn-active': activeTab === tab.key }"
            :style="activeTab === tab.key ? activeTabStyle : null"
            @click="selectTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-if="loading && tasks.length === 0" class="mbst-loading-wrap">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          <p class="text-sm text-gray-500 mt-3">Loading tasks...</p>
        </div>

        <div v-else-if="tasks.length === 0" class="mbst-empty-wrap">
          <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p class="text-sm text-gray-600">{{ emptyMessage }}</p>
        </div>

        <div v-else class="mbst-task-list-wrap">
          <div class="mcl-modal-card mbst-task-list-card">
            <div ref="taskListScroll" class="mbst-task-list">
              <div
                v-for="(task, index) in tasks"
                :key="task.id"
                class="mbst-task-row"
                @click="openTask(task.id)"
              >
                <span class="mbst-task-num" aria-hidden="true">{{ taskListNumber(index) }}</span>
                <div class="mbst-task-row-main">
                  <div class="mbst-task-line-1">
                    <h3 class="mbst-task-title">{{ task.title }}</h3>
                    <span class="mbst-status-pill" :style="statusPillStyle(task.status)">
                      {{ formatStatus(task.status) }}
                    </span>
                  </div>
                  <p class="mbst-task-meta-line">
                    <span class="mbst-meta-segment">
                      <span class="mbst-meta-k">Assignees</span>
                      {{ formatAssignees(task.assignees) }}
                    </span>
                    <span class="mbst-meta-dot">·</span>
                    <span class="mbst-meta-segment">
                      <span class="mbst-meta-k">Created by</span>
                      {{ task.created_by && task.created_by.name ? task.created_by.name : '—' }}
                    </span>
                    <span class="mbst-meta-dot">·</span>
                    <span class="mbst-meta-segment">
                      <span class="mbst-meta-k">Sub-board</span>
                      {{ task.board && task.board.name ? task.board.name : '—' }}
                    </span>
                    <span class="mbst-meta-dot">·</span>
                    <span class="mbst-meta-segment">
                      <span class="mbst-meta-k">Bucket</span>
                      {{ task.column && task.column.name ? task.column.name : '—' }}
                    </span>
                    <span class="mbst-meta-dot">·</span>
                    <span class="mbst-meta-segment">
                      <span class="mbst-meta-k">Due</span>
                      <span :class="{ 'mbst-due-late': isLateDue() }">{{ formatDueDate(task.due_date) }}</span>
                    </span>
                  </p>
                </div>
                <div class="mbst-task-row-action" aria-hidden="true">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <master-board-modal-pagination
            :paginator-info="paginatorInfo"
            :loading="loading && tasks.length > 0"
            :accent-color="paginationAccentColor"
            @page-change="setPage"
          />
        </div>
      </div>
    </base-modal>
  </transition>
</template>

<script>
import gql from 'graphql-tag';
import MasterBoardModalPagination from './MasterBoardModalPagination.vue';

var STATUS_BUCKET_KEYS = ['not_started', 'in_progress', 'late', 'completed'];
var STATUS_BUCKET_COLORS = {
  not_started: '#7C3AED',
  in_progress: '#3B82F6',
  late: '#EF4444',
  completed: '#14B8A6',
};
var STATUS_TAB_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  late: 'Late',
  completed: 'Completed',
};

export default {
  name: 'master-board-member-tasks-modal',
  components: {
    MasterBoardModalPagination,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    masterBoardId: {
      type: [String, Number],
      required: true,
    },
    assigneeUserId: {
      type: [String, Number],
      default: '',
    },
    memberLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      tasks: [],
      paginatorInfo: null,
      currentPage: 1,
      perPage: 10,
      activeTab: 'not_started',
      statusTabs: [
        { key: 'not_started', label: 'Not Started' },
        { key: 'in_progress', label: 'In Progress' },
        { key: 'late', label: 'Late' },
        { key: 'completed', label: 'Completed' },
      ],
    };
  },
  computed: {
    isUnassigned() {
      return String(this.assigneeUserId) === '0';
    },
    modalTitle() {
      if (this.isUnassigned) {
        return 'Unassigned Tasks';
      }
      return (this.memberLabel || 'Member') + ' Tasks';
    },
    tasksCountLabel() {
      if (this.isUnassigned) {
        return 'unassigned tasks across all sub-boards';
      }
      return 'tasks assigned to this member';
    },
    emptyMessage() {
      if (this.isUnassigned) {
        return 'No ' + this.activeTabLabel + ' unassigned tasks.';
      }
      return 'No ' + this.activeTabLabel + ' tasks assigned to ' + (this.memberLabel || 'this member') + '.';
    },
    activeTabLabel() {
      return STATUS_TAB_LABELS[this.activeTab] || this.activeTab;
    },
    headerAccentBg() {
      return '#8B5CF6';
    },
    paginationAccentColor() {
      var color = STATUS_BUCKET_COLORS[this.activeTab];
      return color || '#2563eb';
    },
    activeTabStyle() {
      var color = STATUS_BUCKET_COLORS[this.activeTab];
      if (!color) {
        return null;
      }
      return {
        backgroundColor: color,
        borderColor: color,
        color: '#fff',
      };
    },
  },
  watch: {
    visible: function (isVisible) {
      if (isVisible && this.hasAssigneeFilter()) {
        this.activeTab = 'not_started';
        this.currentPage = 1;
        this.fetchTasks(1);
      }
    },
    assigneeUserId: function (newVal, oldVal) {
      if (this.visible && this.hasAssigneeFilter() && String(newVal) !== String(oldVal)) {
        this.activeTab = 'not_started';
        this.currentPage = 1;
        this.fetchTasks(1);
      }
    },
  },
  methods: {
    hasAssigneeFilter() {
      return this.assigneeUserId !== '' && this.assigneeUserId !== null && this.assigneeUserId !== undefined;
    },
    handleClose() {
      this.$emit('close');
    },
    selectTab(tabKey) {
      if (!tabKey || this.activeTab === tabKey) {
        return;
      }
      if (!STATUS_BUCKET_KEYS.includes(tabKey)) {
        return;
      }
      this.activeTab = tabKey;
      this.currentPage = 1;
      this.fetchTasks(1);
    },
    setPage(page) {
      var nextPage = parseInt(page, 10);
      if (!nextPage || nextPage < 1) {
        return;
      }
      this.fetchTasks(nextPage);
    },
    async fetchTasks(page) {
      if (!this.masterBoardId || !this.hasAssigneeFilter()) {
        return;
      }
      if (!STATUS_BUCKET_KEYS.includes(this.activeTab)) {
        return;
      }

      this.loading = true;
      this.currentPage = page;

      try {
        var result = await this.$apollo.query({
          query: gql`
            query MasterBoardChartMemberTasks(
              $master_board_id: ID!
              $assignee_user_id: ID!
              $chart_status: String!
              $first: Int!
              $page: Int!
            ) {
              masterBoardChartMemberTasks(
                master_board_id: $master_board_id
                assignee_user_id: $assignee_user_id
                chart_status: $chart_status
                first: $first
                page: $page
              ) {
                data {
                  id
                  title
                  status
                  due_date
                  created_by { id name }
                  assignees { id name }
                  board { id name }
                  column { id name }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
          variables: {
            master_board_id: String(this.masterBoardId),
            assignee_user_id: String(this.assigneeUserId),
            chart_status: this.activeTab,
            first: this.perPage,
            page: page,
          },
          fetchPolicy: 'network-only',
        });

        var payload = result.data && result.data.masterBoardChartMemberTasks;
        this.tasks = payload && payload.data ? payload.data : [];
        this.paginatorInfo = payload && payload.paginatorInfo ? payload.paginatorInfo : null;
        this.scrollTaskListToTop();
      } catch (e) {
        console.error('Error fetching master board chart member tasks:', e);
        this.tasks = [];
        this.paginatorInfo = null;
      } finally {
        this.loading = false;
      }
    },
    formatAssignees(assignees) {
      if (!assignees || !assignees.length) {
        return 'Unassigned';
      }
      return assignees.map(function (a) {
        return a.name || 'Unknown';
      }).join(', ');
    },
    formatStatus(status) {
      var labels = {
        todo: 'To Do',
        in_progress: 'In Progress',
        paused: 'Paused',
        in_review: 'In Review',
        done: 'Done',
      };
      var key = (status || 'todo').toLowerCase();
      return labels[key] || status;
    },
    statusPillStyle(status) {
      var colors = {
        todo: { bg: '#EDE9FE', text: '#5B21B6' },
        in_progress: { bg: '#DBEAFE', text: '#1D4ED8' },
        paused: { bg: '#FEF3C7', text: '#B45309' },
        in_review: { bg: '#E0E7FF', text: '#3730A3' },
        done: { bg: '#CCFBF1', text: '#0F766E' },
      };
      var key = (status || 'todo').toLowerCase();
      var c = colors[key] || { bg: '#F3F4F6', text: '#374151' };
      return {
        backgroundColor: c.bg,
        color: c.text,
      };
    },
    formatDueDate(dateStr) {
      if (!dateStr) {
        return '—';
      }
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) {
        return dateStr;
      }
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    isLateDue() {
      return this.activeTab === 'late';
    },
    scrollTaskListToTop() {
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.taskListScroll;
        if (el) {
          el.scrollTop = 0;
        }
      });
    },
    taskListNumber(index) {
      var i = parseInt(index, 10);
      if (isNaN(i) || i < 0) {
        i = 0;
      }
      if (this.paginatorInfo && this.paginatorInfo.firstItem) {
        return this.paginatorInfo.firstItem + i;
      }
      return (this.currentPage - 1) * this.perPage + i + 1;
    },
    openTask(taskId) {
      if (!taskId) {
        return;
      }
      var resolved = this.$router.resolve({ path: '/tasks/task/' + taskId });
      var url = resolved.href;
      if (!url || url.indexOf('http') !== 0) {
        var fp = resolved.fullPath || '';
        if (fp.indexOf('/') !== 0) {
          fp = '/' + fp;
        }
        url =
          window.location.origin +
          (window.location.pathname || '/') +
          (window.location.search || '') +
          '#' +
          fp;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style scoped>
.mcl-modal-header {
  padding: 1.25rem 3rem 1.25rem 1.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%);
  color: #fff;
  border-radius: 1rem 1rem 0 0;
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.mcl-modal-header-inner {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.mcl-modal-header-icon-wrap {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.mcl-modal-header-icon {
  font-size: 1.35rem;
  opacity: 0.95;
}

.mcl-modal-header-title {
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.3;
}

.mcl-modal-header-sub {
  margin: 0.2rem 0 0 0;
  font-size: 0.8125rem;
  color: rgba(219, 234, 254, 0.95);
  line-height: 1.35;
}

.mcl-modal-body {
  padding: 1rem 1.25rem 0.75rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  overflow: hidden;
}

.mbst-modal-body-flex {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.mbpt-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}

.mbpt-tab-btn {
  flex: 1;
  min-width: 0;
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  line-height: 1.2;
  white-space: nowrap;
}

.mbpt-tab-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.mbpt-tab-btn-active {
  color: #fff;
}

.mbpt-tab-btn-active:hover {
  opacity: 0.92;
}

.mcl-modal-card {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
}

.mbst-loading-wrap,
.mbst-empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  flex: 1;
}

.mbst-task-list-wrap {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  gap: 0.5rem;
  overflow: hidden;
}

.mbst-task-list-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0;
}

.mbst-task-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 0.75rem;
  scroll-padding-bottom: 0.75rem;
  -webkit-overflow-scrolling: touch;
}

.mbst-task-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.mbst-task-row:last-child {
  border-bottom: none;
}

.mbst-task-row:hover {
  background-color: #f8fafc;
}

.mbst-task-num {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #475569;
  background: #f1f5f9;
  border-radius: 0.375rem;
  line-height: 1;
}

.mbst-task-row-main {
  flex: 1;
  min-width: 0;
}

.mbst-task-line-1 {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.125rem;
}

.mbst-task-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mbst-status-pill {
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  text-transform: capitalize;
  flex-shrink: 0;
  line-height: 1.2;
}

.mbst-task-meta-line {
  margin: 0;
  font-size: 0.6875rem;
  color: #475569;
  line-height: 1.35;
  word-break: break-word;
}

.mbst-meta-segment {
  display: inline;
  white-space: normal;
}

.mbst-meta-k {
  font-weight: 600;
  color: #94a3b8;
  margin-right: 0.2rem;
}

.mbst-meta-k::after {
  content: ':';
}

.mbst-meta-dot {
  color: #cbd5e1;
  margin: 0 0.2rem;
  user-select: none;
}

.mbst-due-late {
  color: #dc2626;
  font-weight: 600;
}

.mbst-task-row-action {
  flex-shrink: 0;
  align-self: center;
}
</style>
